/**
 * projectItems.transformers.ts
 *
 * Camada de transformação de dados para Project Items
 * Todas as funções são puras (sem side effects) e imutáveis
 *
 * Fluxo de transformação:
 * API Response (snake_case) → Normalized (camelCase) → Form State
 */

import { SelectedSkillStandard } from '../types'

// ========================================
// API Response Types (snake_case)
// ========================================

/** Item individual retornado pela API */
export interface ApiProjectItem {
  id: string
  name: string
  curriculum_kind: 'content' | 'skill'
  breadcrumb: string
  original_codes: string
  color: string
  lv1_name: string
  lv1_id?: string
  lv2_name: string
  lv2_id?: string
  cycles?: Array<{ name: string; id: string }>
}

/** Estrutura que a API retorna em project_items (pode vir em diferentes formatos) */
export interface ApiProjectItems {
  contents: ApiProjectItem[]
  specific_skills?: ApiProjectItem[]  // snake_case (do endpoint GET /api/projects/:id)
  specificSkills?: ApiProjectItem[]   // camelCase (do endpoint show_selected_items)
}

// ========================================
// Normalized Types (camelCase)
// ========================================

/** Item normalizado para uso interno */
export interface NormalizedProjectItem {
  id: string
  name: string
  curriculumKind: 'content' | 'skill'
  breadcrumb: string
  originalCodes: string
  color: string
  lv1Name: string
  lv1Id?: string
  lv2Name: string
  lv2Id?: string
  cycles?: Array<{ name: string; id: string }>
}

// ========================================
// Pure Transformation Functions
// ========================================

/**
 * Normaliza um item da API (aceita tanto snake_case quanto camelCase)
 * @pure - não altera o objeto original
 * @param item - Item da API (pode estar em snake_case ou camelCase)
 * @returns Item normalizado em camelCase
 */
export const normalizeApiProjectItem = (
  item: any
): NormalizedProjectItem => ({
  id: item.id,
  name: item.name,
  // Aceita ambos os formatos
  curriculumKind: item.curriculumKind || item.curriculum_kind,
  breadcrumb: item.breadcrumb,
  originalCodes: item.originalCodes || item.original_codes,
  color: item.color,
  lv1Name: item.lv1Name || item.lv1_name,
  lv1Id: item.lv1Id || item.lv1_id,
  lv2Name: item.lv2Name || item.lv2_name,
  lv2Id: item.lv2Id || item.lv2_id,
  cycles: item.cycles,
})

/**
 * Achata contents e specific_skills em um único array normalizado
 * @pure - cria novo array sem mutar originais
 * @param projectItems - Objeto contendo contents e specific_skills da API
 * @returns Array normalizado com todos os items
 */
export const normalizeProjectItems = (
  projectItems: ApiProjectItems | undefined
): NormalizedProjectItem[] => {
  if (!projectItems) return []

  const contents = (projectItems.contents || []).map(item => ({
    ...normalizeApiProjectItem(item),
    curriculumKind: 'content' as const,
  }))

  // Aceita tanto specific_skills (snake_case) quanto specificSkills (camelCase)
  const skillsArray = projectItems.specific_skills || projectItems.specificSkills || []
  const skills = skillsArray.map(item => ({
    ...normalizeApiProjectItem(item),
    curriculumKind: 'skill' as const,
  }))

  return [...contents, ...skills]
}

/**
 * Transforma item normalizado para o formato do formulário
 * @pure - mapeia 1:1 preservando todos os dados
 * @param item - Item normalizado
 * @returns Item no formato SelectedSkillStandard
 */
export const transformToFormSkillStandard = (
  item: NormalizedProjectItem
): SelectedSkillStandard => ({
  id: item.id,
  name: item.name,
  type: item.curriculumKind,
  breadcrumb: item.breadcrumb,
  originalCodes: item.originalCodes,
  color: item.color,
  lv1Name: item.lv1Name,
  lv2Name: item.lv2Name,
  lv1ID: item.lv1Id,
  lv2ID: item.lv2Id,
  cycles: item.cycles,
})

/**
 * Pipeline completo: API Response → Form State
 * @pure - composição de funções puras
 * @param projectItems - Objeto project_items da API
 * @returns Array de SelectedSkillStandard pronto para uso no formulário
 *
 * @example
 * const apiResponse = {
 *   project_items: {
 *     contents: [{ id: '1', name: 'Content', curriculum_kind: 'content', ... }],
 *     specific_skills: [{ id: '2', name: 'Skill', curriculum_kind: 'skill', ... }]
 *   }
 * }
 * const formState = transformApiProjectItemsToFormState(apiResponse.project_items)
 * // formState = [{ id: '1', type: 'content', ... }, { id: '2', type: 'skill', ... }]
 */
export const transformApiProjectItemsToFormState = (
  projectItems: ApiProjectItems | undefined
): SelectedSkillStandard[] => {
  return normalizeProjectItems(projectItems)
    .map(transformToFormSkillStandard)
}

// ========================================
// Immutable State Operations
// ========================================

/**
 * Adiciona items ao estado de forma imutável
 * Remove duplicatas baseado no ID
 * @pure - retorna novo array sem mutar original
 * @param current - Array atual de items
 * @param newItems - Novos items a adicionar
 * @returns Novo array com items únicos adicionados
 */
export const addSkillsStandards = (
  current: SelectedSkillStandard[],
  newItems: SelectedSkillStandard[]
): SelectedSkillStandard[] => {
  const existingIds = new Set(current.map(item => item.id))
  const uniqueNewItems = newItems.filter(item => !existingIds.has(item.id))
  return [...current, ...uniqueNewItems]
}

/**
 * Remove item por ID de forma imutável
 * @pure - retorna novo array filtrado
 * @param current - Array atual de items
 * @param idToRemove - ID do item a remover
 * @returns Novo array sem o item removido
 */
export const removeSkillStandard = (
  current: SelectedSkillStandard[],
  idToRemove: string
): SelectedSkillStandard[] => {
  return current.filter(item => item.id !== idToRemove)
}

/**
 * Atualiza item específico de forma imutável
 * @pure - retorna novo array com item atualizado
 * @param current - Array atual de items
 * @param id - ID do item a atualizar
 * @param updates - Atualizações parciais a aplicar
 * @returns Novo array com item atualizado
 */
export const updateSkillStandard = (
  current: SelectedSkillStandard[],
  id: string,
  updates: Partial<SelectedSkillStandard>
): SelectedSkillStandard[] => {
  return current.map(item =>
    item.id === id ? { ...item, ...updates } : item
  )
}

/**
 * Extrai apenas IDs para envio à API
 * @pure - mapeia array para array de strings
 * @param items - Array de items
 * @returns Array de IDs
 */
export const extractItemIds = (
  items: SelectedSkillStandard[]
): string[] => {
  return items.map(item => item.id)
}

/**
 * Substitui todo o array de forma imutável
 * Útil para resetar ou carregar novos dados
 * @pure - retorna novo array
 * @param newItems - Novos items a definir
 * @returns Novo array com os items fornecidos
 */
export const replaceSkillsStandards = (
  newItems: SelectedSkillStandard[]
): SelectedSkillStandard[] => {
  return [...newItems]
}

/**
 * Filtra items por tipo
 * @pure - retorna novo array filtrado
 * @param items - Array de items
 * @param type - Tipo a filtrar ('content' ou 'skill')
 * @returns Novo array apenas com items do tipo especificado
 */
export const filterByType = (
  items: SelectedSkillStandard[],
  type: 'content' | 'skill'
): SelectedSkillStandard[] => {
  return items.filter(item => item.type === type)
}

/**
 * Conta items por tipo
 * @pure - não altera o array
 * @param items - Array de items
 * @returns Objeto com contadores { contents: number, skills: number }
 */
export const countByType = (
  items: SelectedSkillStandard[]
): { contents: number; skills: number } => {
  return items.reduce(
    (acc, item) => {
      if (item.type === 'content') {
        acc.contents++
      } else {
        acc.skills++
      }
      return acc
    },
    { contents: 0, skills: 0 }
  )
}
